// Inventory API on the shared apiClient (base URL, auth, error semantics).
// Replaces the inventory half of the retired services/api.ts ApiService;
// method signatures and return shapes are kept identical so call sites only
// swapped imports. The three read helpers (breakdown / layers / case plan)
// used to swallow failures and return an empty shape — a failed read rendered
// as "no layers / nothing allocated", which is a statement about the stock,
// not about the network. They throw now and the audit drawer says it could
// not load (CodeRabbit, 2026-08-21).
//
// PDF invoice upload is NOT here: /vendor-invoices/upload-single is owned by
// lib/vendorInvoiceApi.ts (`uploadVendorInvoiceSingle`).
import { deleteRequest, get, postJson, put } from './apiClient';
// The client's shared number parser (mirrored twin of utils/num's contract):
// null for missing, never a silent 0 — the fallback is written out here.
import { num } from '../utils/invoiceLineMath';

export type InventoryLocation = 'UNITED' | 'PANTRY';

// One SKU/item record in our system
export interface InventoryItem {
  _id?: string;
  id?: string;

  // Basic identity
  sku?: string;
  name: string;
  category?: string;
  subCategory?: string;
  packaging?: string;
  unit?: string;
  source?: 'manual' | 'invoice' | 'pantry';

  // UOM concept: Level 2 + Level 1 (Pack vs Items)
  // Example: "2 Pack" where packSize = 12 and baseUnit = "1 Ltr"
  uomLevel2Name?: string; // e.g. "Pack"
  uomLevel2Qty?: number; // e.g. 2
  uomLevel1Name?: string; // e.g. "Item"
  uomLevel1Qty?: number; // e.g. 24 (derived / stored)
  packSize?: number; // e.g. 12 (how many items per pack)
  baseUnit?: string; // e.g. "1 Ltr"

  // The nested shape the server actually stores and returns (InventoryItem.uom
  // in src/models/InventoryItem.js) — the flat uomLevel2Name/etc. fields above
  // are the legacy create-form vocabulary `toInventoryPayload` translates from,
  // never what a GET returns. `level1Name` is the COUNT-level unit (e.g. "PCS"),
  // never the per-piece SIZE unit — that lives in weightUnit/weightPerLevel1
  // (e.g. a 320g piece is weightUnit:'kg', weightPerLevel1:0.32), which is how
  // "14 PCS of 320g each" is told apart from a case literally priced per gram.
  uom?: {
    level2Name?: string;
    level1Name?: string;
    level1PerLevel2?: number;
    weightUnit?: 'kg' | 'lb' | null;
    weightPerLevel1?: number | null;
  };

  // Stock
  location?: InventoryLocation; // optional UI hint
  quantityLevel1?: number; // total items (level 1)
  quantityLevel2?: number; // optional packs (level 2) if you store it too

  // Cost tracking
  costPerUnit?: number; // cost per level1 unit
  currency?: string;
  priceTT?: number;
  priceUSD?: number;

  // Expiry
  expiryDate?: string; // manual set (YYYY-MM-DD)
  expiryAlertDays?: number; // e.g. 30

  // Audit
  createdAt?: string;
  updatedAt?: string;
}

export interface InventoryMovement {
  _id?: string;
  id?: string;

  itemId: string;

  // TRANSFER / ADJUST / PURCHASE / CONSUME / RETURN / ALLOCATE_TO_SHIP
  type: string;

  fromLocation?: InventoryLocation;
  toLocation?: InventoryLocation;

  // quantities always stored in Level 1 for accuracy
  quantityLevel1: number;

  // optional metadata
  shipId?: string;
  shipName?: string;
  referenceType?: string; // e.g. vendor_invoice
  referenceId?: string; // invoice id
  note?: string;

  // Approval flow
  status?: 'pending' | 'approved' | 'rejected';
  requestedBy?: string;
  approvedBy?: string;
  approvedAt?: string;

  createdAt?: string;
}

export interface CreateInventoryItemData {
  name: string;
  category?: string;
  subCategory?: string;
  packaging?: string;
  unit?: string;
  sku?: string;
  source?: 'manual' | 'invoice' | 'pantry';

  uomLevel2Name?: string;
  uomLevel2Qty?: number;
  uomLevel1Name?: string;
  uomLevel1Qty?: number;
  packSize?: number;
  baseUnit?: string;
  // Structured override — when set, `toInventoryPayload` uses this verbatim
  // instead of deriving `uom` from the flat fields above. See InventoryItem.uom.
  uom?: {
    level2Name?: string;
    level1Name?: string;
    level1PerLevel2?: number;
    weightUnit?: 'kg' | 'lb' | null;
    weightPerLevel1?: number | null;
  };

  quantityLevel1?: number;
  costPerUnit?: number;
  currency?: string;
  priceTT?: number;
  priceUSD?: number;

  expiryDate?: string;
  expiryAlertDays?: number;
}

export interface UpdateInventoryItemData {
  name?: string;
  category?: string;
  subCategory?: string;
  packaging?: string;
  unit?: string;
  sku?: string;
  source?: 'manual' | 'invoice' | 'pantry';

  uomLevel2Name?: string;
  uomLevel2Qty?: number;
  uomLevel1Name?: string;
  uomLevel1Qty?: number;
  packSize?: number;
  baseUnit?: string;
  uom?: {
    level2Name?: string;
    level1Name?: string;
    level1PerLevel2?: number;
    weightUnit?: 'kg' | 'lb' | null;
    weightPerLevel1?: number | null;
  };
  pricing?: { priceTT?: number; priceUSD?: number };

  // No stock fields here ON PURPOSE: balances move only through
  // InventoryMovement (invariant 1), and the server's sanitizeItemUpdate drops
  // `stock.*` from an item update anyway — a caller must not be invited to try.

  costPerUnit?: number;
  currency?: string;
  priceTT?: number;
  priceUSD?: number;

  expiryDate?: string;
  expiryAlertDays?: number;
}

/** One goods receipt still on the shelf. */
export interface StockLayerDto {
  _id: string;
  location: 'united' | 'pantry';
  qtyReceivedLevel1: number;
  qtyRemainingLevel1: number;
  qtyReservedLevel1: number;
  expiryDate: string | null;
  receivedAt: string;
  costPerLevel1: number;
  currency: string;
  uomLevel1Name?: string;
  // Both already arrive: `listOpenLayers` returns the layer document unprojected
  // (`.lean()` with no field list), so these were in the browser all along and
  // only this interface dropped them: the same shape as TECH_DEBT #51, where
  // the movement's invoice id reaches the page and just is not rendered.
  //
  // They are what lets a layer show its case count next to its level-1 figure.
  // Needed because the level-1 NAME is not trustworthy on its own: on prod row
  // 6a83a775b9593c6f045095fd it is "OZ", lifted out of the `8.62OZ` in the
  // product size, so a count of 8 renders as a weight (TECH_DEBT #50).
  uomLevel2Name?: string;
  level1PerLevel2?: number;
  sourceInvoiceNumber?: string;
  sourceVendorName?: string;
  originalVesselName?: string;
  backfilled?: boolean;
  /**
   * Goods held on this batch for vendor returns (D44, VR-3): still counted in
   * `qtyRemainingLevel1`, but no allocation may take them. The sum of
   * `returnHolds`, written only by the server (stockReturnHold.service).
   */
  qtyHeldLevel1?: number;
  returnHolds?: Array<{
    debitNoteNumber?: string;
    lineNumber?: number | null;
    qtyLevel1: number;
    qtyLevel2?: number;
    caseSeqs?: number[];
  }>;
}

/**
 * One physical box of catch-weight goods.
 *
 * Meat is billed by weight but delivered as cases of unequal weight — a 119.5 kg
 * line of ribeye is nine boxes weighing 14.40, 12.10, 13.00 and so on. These are
 * those boxes, so a vessel asking for 50 kg can be told which ones to carry out.
 */
export interface StockCaseDto {
  layerId: string;
  seq: number;
  code: string;
  weightLevel1: number;
  weightRemainingLevel1: number;
  opened: boolean;
  /** `held`: set aside for a vendor return (D44). Shown, never pickable. */
  status: 'available' | 'reserved' | 'held' | 'consumed';
  vesselName?: string;
  expiryDate: string | null;
  receivedAt: string | null;
  costPerLevel1: number;
  currency: string;
  sourceInvoiceNumber?: string;
  uomLevel1Name?: string;
}

/** Which boxes to combine, and how close that gets to what was asked for. */
export interface CasePickPlan {
  picks: StockCaseDto[];
  targetLevel1: number;
  total: number;
  count: number;
  overage: number;
  shortfall: number;
  satisfied: boolean;
  exact: boolean;
  method: 'dp' | 'greedy_swap' | 'all_available' | 'none';
  candidateCount: number;
  alternatives: Array<{ ids: string[]; total: number; count: number }>;
}

export interface CasePlanResponse {
  cases: StockCaseDto[];
  plan: CasePickPlan | null;
  /** 'no_case_data' on every item that is not catch weight — which is most of them. */
  fallback: string | null;
  /** Why a catch-weight receipt has no boxes, when it should have had some. */
  note: string | null;
  uomLevel1Name: string;
}

export interface StockLayerAvailability {
  layers: StockLayerDto[];
  totalAvailableLevel1: number;
  totalRemainingLevel1: number;
  totalReservedLevel1: number;
  /** Held for vendor returns across the layers (D44): remaining, but not available. */
  totalHeldLevel1?: number;
  hasUndatedLayers: boolean;
}

export interface StockLayerReconciliation {
  itemId: string;
  itemName: string;
  drifted: boolean;
  locations: Record<
    'united' | 'pantry',
    { layerTotalLevel1: number; stockTotalLevel1: number; driftLevel1: number; layerCount: number }
  >;
}

export interface CreateInventoryMovementData {
  itemId: string;
  type: string;
  fromLocation?: InventoryLocation;
  toLocation?: InventoryLocation;
  quantityLevel1: number;
  shipId?: string;
  shipName?: string;
  referenceType?: string;
  referenceId?: string;
  note?: string;
}

// The create form speaks in flat legacy fields (packSize, quantityLevel1,
// costPerUnit…) while the backend stores nested uom/stock/costing/pricing.
// Build the nested shape when the caller has not already provided it. It builds NO
// `stock` and NO `costing`: a new item opens at zero and costs from its receipts,
// and the server refuses both on create anyway (TECH_DEBT #313, hard invariant 1).
function toInventoryPayload(payload: any) {
  if (!payload) return payload;

  const uom = payload.uom || {
    level2Name: payload.uomLevel2Name || payload.packaging || 'Pack',
    level1Name: payload.uomLevel1Name || payload.unit || 'Item',
    level1PerLevel2: num(payload.packSize) ?? num(payload.uomLevel1Qty) ?? 1,
  };

  const pricing = payload.pricing || {
    priceTT: num(payload.priceTT) ?? 0,
    priceUSD: num(payload.priceUSD) ?? 0,
  };

  return {
    ...payload,
    uom,
    pricing,
  };
}

// D95.5 — which sidebar entry is asking. The server maps it to `source`
// (utils/inventoryDivision): ship = manual + invoice, pantry = pantry.
export type InventoryDivision = 'ship' | 'pantry';

// UNITED inventory list (self inventory)
export async function getInventoryItems(params?: {
  q?: string;
  lowStockOnly?: boolean;
  source?: string;
  division?: InventoryDivision;
  category?: string;
  subCategory?: string;
  expiryFilter?: string;
  page?: number;
  limit?: number;
}): Promise<{ items: InventoryItem[]; total: number; page: number; limit: number }> {
  const query = new URLSearchParams();
  if (params?.q) query.set('q', params.q);
  if (params?.lowStockOnly) query.set('lowStockOnly', 'true');
  if (params?.source) query.set('source', params.source);
  if (params?.division) query.set('division', params.division);
  if (params?.category) query.set('category', params.category);
  if (params?.subCategory) query.set('subCategory', params.subCategory);
  if (params?.expiryFilter) query.set('expiryFilter', params.expiryFilter);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));

  const path = `/inventory/items${query.toString() ? `?${query.toString()}` : ''}`;
  const data = await get<any>(path, { cache: 'no-store' });
  const list = data?.data?.items ?? data?.data ?? [];
  return {
    items: Array.isArray(list) ? list : [],
    total: num(data?.data?.total) ?? 0,
    page: num(data?.data?.page) ?? 1,
    limit: num(data?.data?.limit) ?? (params?.limit || 50),
  };
}

// D108.3: one item by id (GET /inventory/items/:id), so a linked stock item can be named.
export async function getInventoryItem(itemId: string): Promise<InventoryItem | null> {
  const data = await get<any>(`/inventory/items/${encodeURIComponent(itemId)}`, { cache: 'no-store' });
  return data?.data?.item ?? null;
}

// USC-BUG-0042 — Category / Sub Category filter options over the whole filtered
// list (server: inventory.service `listItemFacets`), not the page on screen.
export async function getInventoryFacets(params?: {
  source?: string;
  division?: InventoryDivision;
  category?: string;
}): Promise<{ categories: string[]; subCategories: string[] }> {
  const query = new URLSearchParams();
  if (params?.source) query.set('source', params.source);
  if (params?.division) query.set('division', params.division);
  if (params?.category) query.set('category', params.category);
  const path = `/inventory/items/facets${query.toString() ? `?${query.toString()}` : ''}`;
  const data = await get<any>(path, { cache: 'no-store' });
  const strings = (v: unknown) => (Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []);
  return { categories: strings(data?.data?.categories), subCategories: strings(data?.data?.subCategories) };
}

export async function createInventoryItem(payload: CreateInventoryItemData): Promise<InventoryItem> {
  const data = await postJson<any>('/inventory/items', toInventoryPayload(payload));
  return data?.data ?? data;
}

export async function updateInventoryItem(
  itemId: string,
  payload: UpdateInventoryItemData
): Promise<InventoryItem> {
  const data = await put<any>(`/inventory/items/${itemId}`, payload);
  return data?.data ?? data;
}

export async function deleteInventoryItem(itemId: string): Promise<void> {
  await deleteRequest(`/inventory/items/${itemId}`);
}

// Movements (purchase, adjust, transfer, allocate, return)
export async function getInventoryMovements(params?: {
  itemId?: string;
  status?: string;
}): Promise<InventoryMovement[]> {
  const query = new URLSearchParams();
  if (params?.itemId) query.set('itemId', params.itemId);
  if (params?.status) query.set('status', params.status);

  const path = `/inventory/movements${query.toString() ? `?${query.toString()}` : ''}`;
  const data = await get<any>(path);
  const list = data?.data?.movements ?? data?.data ?? [];
  return Array.isArray(list) ? list : [];
}

export async function getVesselAllocationBreakdown(itemId: string): Promise<{
  totalAllocated: number;
  perVessel: Record<string, number>;
  perVesselArray: Array<{ vessel: string; qty: number }>;
}> {
  const data = await get<any>(`/inventory/items/${itemId}/vessel-allocations`);
  return data?.data?.breakdown ?? { totalAllocated: 0, perVessel: {}, perVesselArray: [] };
}

/**
 * The individual goods receipts making up an item's balance, in the order
 * they would be consumed (oldest expiry first, undated last).
 *
 * `reconciliation` compares the layer totals against the item's own stock
 * figure. A non-zero drift means stock was changed without a movement behind
 * it — the Yelo pantry overwrite, a direct edit, or a case-break — and is
 * worth showing rather than hiding.
 */
export async function getItemStockLayers(itemId: string): Promise<{
  united: StockLayerAvailability;
  pantry: StockLayerAvailability;
  reconciliation: StockLayerReconciliation | null;
}> {
  const empty: StockLayerAvailability = {
    layers: [],
    totalAvailableLevel1: 0,
    totalRemainingLevel1: 0,
    totalReservedLevel1: 0,
    totalHeldLevel1: 0,
    hasUndatedLayers: false,
  };
  const data = await get<any>(`/inventory/items/${itemId}/layers`);
  return {
    united: data?.data?.united ?? empty,
    pantry: data?.data?.pantry ?? empty,
    reconciliation: data?.data?.reconciliation ?? null,
  };
}

/**
 * The individual boxes behind a catch-weight balance, and — when a target is
 * given — which of them to combine to reach it.
 *
 * Read-only. Asking "what makes 50 kg" reserves nothing; the answer is
 * committed by allocateCasesToShip below.
 */
export async function getItemCasePlan(
  itemId: string,
  opts: { location?: 'united' | 'pantry'; target?: number } = {}
): Promise<CasePlanResponse> {
  const empty: CasePlanResponse = {
    cases: [],
    plan: null,
    fallback: 'no_case_data',
    note: null,
    uomLevel1Name: '',
  };
  const params = new URLSearchParams({ location: opts.location || 'united' });
  if (opts.target && opts.target > 0) params.set('target', String(opts.target));

  const data = await get<any>(`/inventory/items/${itemId}/cases?${params.toString()}`);
  return { ...empty, ...(data?.data || {}) };
}

/**
 * Send named boxes to a vessel.
 *
 * No quantity is sent: the weight is whatever those boxes weigh, so the
 * shipped figure and the goods that left cannot drift apart. A 409 means
 * another picker claimed one of them first.
 */
export async function allocateCasesToShip(
  itemId: string,
  payload: {
    location?: 'united' | 'pantry';
    vesselName: string;
    caseRefs: Array<{ layerId: string; seq: number }>;
    note?: string;
  }
): Promise<{ movementId: string; level1Qty: number; caseCount: number }> {
  const data = await postJson<any>(`/inventory/items/${itemId}/allocate-cases`, payload);
  return data?.data;
}

// D113.5: one row of a split across vessels — a quantity, OR named boxes
// (catch weight: the weight follows from the boxes, so no qty is sent).
export type VesselSplitRow =
  | { vesselName: string; qty: number; note?: string }
  | { vesselName: string; caseRefs: Array<{ layerId: string; seq: number }>; note?: string };

export interface VesselSplitResultRow {
  row: number;
  vesselName: string;
  movementId: string;
  level1Qty: number;
  caseCount?: number;
}

/**
 * Split one item across several vessels in ONE request (D113.5): every row
 * moves or none does. A refusal names the row it stopped on in
 * `err.details.row` (1-based, in the order sent) and `err.details.vesselName`.
 */
export async function allocateSplitToVessels(
  itemId: string,
  payload: { location: 'united' | 'pantry'; rows: VesselSplitRow[] }
): Promise<{ rows: VesselSplitResultRow[] }> {
  const data = await postJson<any>(`/inventory/items/${itemId}/split-to-vessels`, payload);
  return data?.data;
}

// Pull retained units between vessels. A 200 always carries success:true (the
// controller never answers 200 with success:false); refusals throw.
export async function pullRetained(
  itemId: string,
  body: { fromVesselName: string; toVesselName: string; level1Qty: number; reason?: string }
): Promise<any> {
  const data = await postJson<any>(`/inventory/items/${itemId}/pull-retained`, body);
  return data?.data;
}

// Turn sealed cases into loose units at one location.
export async function breakCase(
  itemId: string,
  body: { location: 'united' | 'pantry'; cases: number; reason?: string }
): Promise<{ looseUnitsProduced?: number }> {
  const data = await postJson<any>(`/inventory/items/${itemId}/break-case`, body);
  return data?.data;
}

// Super-admin HOLD lifecycle: set, clear, confirm (confirm deducts pantry stock).
export async function setHold(itemId: string, body: { level1: number; reason: string }): Promise<any> {
  const data = await postJson<any>(`/inventory/items/${itemId}/hold`, body);
  return data?.data;
}

export async function clearHold(itemId: string): Promise<any> {
  const data = await deleteRequest<any>(`/inventory/items/${itemId}/hold`);
  return data?.data;
}

export async function confirmHold(itemId: string): Promise<any> {
  const data = await postJson<any>(`/inventory/items/${itemId}/hold/confirm`, {});
  return data?.data;
}

export async function createInventoryMovement(
  payload: CreateInventoryMovementData
): Promise<InventoryMovement> {
  const data = await postJson<any>('/inventory/movements', payload);
  return data?.data ?? data;
}

// D113.3: what POST /inventory/movements/apply-now takes — the server's own
// movement shape, not the legacy flat fields above. `requireApproval` must be
// false: a movement that waits for approval cannot be applied in one step.
export interface ApplyNowMovementData {
  itemId: string;
  type: string;
  qty: { level1Qty: number; level2Qty: number };
  from: { location: 'united' | 'pantry' };
  to: { location: 'united' | 'pantry' | 'ship'; shipName?: string };
  requireApproval: false;
  reason?: string;
  note?: string;
}

// D113.3: raise a movement and move the stock in one server transaction. If
// the server refuses (short stock, goods held for a vendor return) nothing is
// written — no APPROVED movement is left behind waiting to be applied.
export async function createAndApplyInventoryMovement(
  payload: ApplyNowMovementData
): Promise<{ movement: InventoryMovement; item: InventoryItem }> {
  const data = await postJson<any>('/inventory/movements/apply-now', payload);
  return data?.data ?? data;
}

// D2-D3: commit a movement to stock (allocate, transfer, etc.). Backend route: POST /movements/:id/apply
export async function applyInventoryMovement(movementId: string): Promise<any> {
  const data = await postJson<any>(`/inventory/movements/${movementId}/apply`, {});
  return data?.data ?? data;
}
